<template>
  <div class="c-autocomplete o-layout" ref="autocomplete">
    <div class="o-layout__cell o-layout__cell--fill">
      <label for="searchquery" class="u-visually-hidden">
        {{ $label('search') }}
      </label>

      <div class="c-autocomplete__enhanced">
        <div
          class="c-search-field"
          :class="{
            'can-get-geo': this.locationAllowed,
            'has-input': this.hasSearch,
          }"
        >
          <input
            :placeholder="$label('placeholder')"
            class="c-input-text c-search-field__input"
            aria-owns="autocomplete-options--destination"
            autocapitalize="none"
            type="text"
            autocomplete="off"
            aria-autocomplete="list"
            role="combobox"
            id="searchquery"
            :value="queryGet"
            @input="querySet"
            @keydown.enter="onEnter"
            @keydown.esc="onLeave"
            @keydown.tab="onTab"
            @keydown.down="onArrowDown"
            @keydown.up="onArrowUp"
            data-cy="unit-search-query"
          />
          <icon icon="magnifier" modifier="c-search-field__icon" />
          <button
            type="button"
            class="o-button-clean c-search-field__clear"
            v-if="this.hasSearch"
            @click="onClearButtonClicked"
            data-cy="unit-search-clear-input"
          >
            <icon icon="cross" modifier="c-search-field__clear-icon" />
          </button>

          <button
            type="button"
            class="o-button-clean c-search-field__get-geo"
            :class="isCurrentLocationSearch ? 'is-active' : ''"
            v-show="this.locationAllowed"
            :title="$label('useCurrentLocation')"
            @click="getLocation"
            data-cy="unit-search-get-geo"
          >
            <icon icon="geo" />
          </button>
        </div>
        <ul
          class="o-list-clean c-autocomplete__list"
          id="autocomplete-options--destination"
          role="listbox"
          :hidden="!isOpen"
          data-cy="unit-search-ac-list"
        >
          <li
            v-if="suggestion"
            class="c-autocomplete__suggestion"
            tabindex="-1"
          >
            <div class="o-layout o-layout--align-middle">
              <div class="o-layout__cell o-layout__cell--fit">
                <icon :icon="suggestion.icon" modifier="c-icon--medium" />
              </div>
              <div class="o-layout__cell o-layout__cell--fill">
                <span>{{ suggestion.label }}</span>
              </div>
            </div>
          </li>
          <li
            v-for="option in autocompleteOptions"
            class="c-autocomplete__option"
            :class="{ 'c-autocomplete__option--active': isActive(option) }"
            role="option"
            tabindex="-1"
            :aria-selected="isActive(option)"
            :key="option.id"
            @click="chooseOption(option)"
            data-cy="unit-search-ac-option"
          >
            {{ option.name }}
            <br />
            <small>{{ getTypeLabel(option) }}</small>
          </li>
        </ul>
        <div aria-live="polite" role="status" class="u-visually-hidden">
          <span class></span> resultaten.
        </div>
      </div>
    </div>
    <div class="o-layout__cell o-layout__cell--fit">
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import UnitService from '@/services/UnitService.js'
import Icon from '@/components/Utilities/Icon'

export default {
  name: 'SearchQuery',
  components: { Icon },
  data() {
    return {
      locationAllowed: navigator.geolocation ? true : false,
      error: false,
      autocompleteTimout: undefined,
      showSuggestion: false,
    }
  },
  computed: Object.assign(
    mapState({
      autocompleteOptions: (state) => state.autocompleteOptions,
      placeQuery: (state) => state.filter.place,
      placeObject: (state) => state.filter.placeObject,
      autocompleteError: (state) => state.autocompleteError,
    }),
    {
      hasSearch() {
        return this.placeQuery !== ''
      },
      isCurrentLocationSearch() {
        return this.placeQuery === this.$label('currentLocation')
      },
      isOpen() {
        return this.autocompleteOptions.length > 0 || this.autocompleteError
      },
      currentOptionIndex() {
        return this.autocompleteOptions.findIndex((option) => {
          return (
            this.placeObject &&
            this.placeObject.locationId === option.locationId
          )
        })
      },
      suggestion() {
        if (this.showSuggestion) {
          return {
            label: this.$label('suggestionLabel'),
            icon: 'arrow-right',
          }
        }
        if (this.autocompleteError) {
          return {
            label: this.$label('autocompleteErrorLabel'),
            icon: 'cross',
          }
        }
      },
      /* TEXT INPUT FIELD GETTER */
      queryGet() {
        // The value is always state.filter.placeQuery in the store
        return this.placeQuery
      },
    }
  ),
  methods: {
    /* TEXT INPUT FIELD SETTER */
    querySet(e) {
      // When we type in the input field, we want to add some logic
      const value = e.target.value
      // Remove search error if any
      if (this.autocompleteError) {
        this.$store.commit('UPDATE_AUTOCOMPLETE_ERROR')
      }

      // Autocomplete options on input, with throttling
      if (value.length > 2) {
        if (this.autocompleteTimout) {
          clearTimeout(this.autocompleteTimout)
        }
        this.autocompleteTimout = setTimeout(() => {
          this.$store.dispatch('getAutoCompleteOptions', this.placeQuery)
        }, 300)
      } else {
        this.closeFoldout()
      }

      if (value.length === 0) {
        this.setOption(undefined, true)
        this.closeFoldout()
        //this.$store.dispatch('getUnits')
      }

      // Remove the chosen place if there is one, and update the query in the store
      if (this.placeObject) {
        this.$store.commit('UPDATE_PLACE_OBJECT')
      }
      // Actually update input value in the state
      this.$store.commit('UPDATE_PLACE_QUERY', value)
    },
    /* KEYBOARD EVENTS */
    onEnter() {
      /*
          If suggestions are visible close suggestions if there is one selected,
          if the suggestions are closed, try to submit the form
        */
      if (this.isOpen) {
        if (this.placeObject) {
          // Remove the autocomplete options to close the foldout.
          this.closeFoldout()
          this.chooseOption(this.placeObject)
        } else {
          // if no place is selected, select the first one and fetch lat / long
          // this.setOption(this.autocompleteOptions[0])
          this.chooseOption(this.autocompleteOptions[0])
          // if there is more then one option, show suggestion
          // if there is only one, close the foldout
          if (this.autocompleteOptions.length > 1) {
            this.showSuggestion = true
          } else {
            this.closeFoldout()
            this.$store.dispatch('getUnits')
          }
        }
      } else {
        if (this.$settings('onlyFilter')) {
          // Only submit the form if we are on home
          this.$store.dispatch('submitForm')
        }
      }
    },
    onArrowDown() {
      if (this.isOpen) {
        const goToIndex = this.currentOptionIndex + 1
        if (goToIndex < this.autocompleteOptions.length) {
          this.setOption(this.autocompleteOptions[goToIndex])
        }
      }
    },
    onArrowUp() {
      if (this.isOpen) {
        const goToIndex = this.currentOptionIndex - 1
        if (goToIndex >= 0) {
          this.setOption(this.autocompleteOptions[goToIndex])
        }
      }
    },
    onTab(e) {
      // Use the same functionality as the down arrow, but tab out of it at the end of the list.
      if (this.currentOptionIndex < this.autocompleteOptions.length - 1) {
        e.preventDefault()
        this.onArrowDown()
      } else {
        this.closeFoldout()
      }
    },
    onLeave() {
      this.closeFoldout()
    },

    /* OTHER CLICK EVENTS */
    onClearButtonClicked() {
      // Clear place state in store
      this.setOption(undefined, true)
      this.closeFoldout()
    },
    async chooseOption(option) {
      // if latlng is not present for the chosen option, fetch it
      if (!option.lat && !option.lng) {
        const geo = await UnitService.getGeo(option.locationId)
        option.lat = geo.data.lat
        option.lng = geo.data.lng
      }
      this.setOption(option, true)
    },
    setOption(option, getNewUnits) {
      this.$store.dispatch('updatePlace', option)
      if (getNewUnits) {
        this.$store.dispatch('getUnits')
        this.closeFoldout()
      }
    },
    closeFoldout() {
      this.showSuggestion = false
      this.$store.commit('UPDATE_AUTOCOMPLETE_OPTIONS', [])
    },

    /* SUBCOMPONENT STATE */
    isActive(option) {
      // Active state for options
      return (
        this.placeObject && this.placeObject.locationId === option.locationId
      )
    },
    getTypeLabel(item) {
      switch (item.locationType) {
        case 0:
          return this.$label('project')
        case 1:
          return this.$label('area')
        case 2:
          return this.$label('poi')
        case 3:
          return this.$label('street')
        default:
          return this.$label('city')
      }
    },

    /* LOCATION LOGIC */
    getLocation: function () {
      navigator.geolocation.getCurrentPosition(this.locationChosen)
    },
    locationChosen: function (position) {
      const option = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        type: 1,
        locationType: 1,
        name: this.$label('currentLocation'),
      }
      this.setOption(option, true)
      this.$store.dispatch('updateRadius', 20)
    },
  },
}
</script>
